export interface SvgInteractionHandlers {
  /**
   * Badge veya renkli parsel gövdesi tıklanınca çağrılır. Kat + kategori,
   * tıklanan şeyin O AN gösterdiği kiracıyı işaret eder (filtre altında bu
   * giriş katı olmayabilir) — sidebar doğru sekmeyle açılsın diye.
   */
  onParcelClick: (locationId: string, level?: string, category?: string) => void;
  onEmptyClick?: () => void;
}

export interface Rect { x: number; y: number; width: number; height: number; }

export interface SvgInteractionOptions {
  /** Gezilebilir sınır — pan/zoom bunun dışına çıkamaz (alt kara, deniz sınırlı) */
  bounds: Rect;
  /** En fazla yakınlaşma (en küçük viewBox genişliği) */
  minWidth?: number;
}

export interface MapController {
  zoomIn(): void;
  zoomOut(): void;
  reset(): void;
  /** Bir parsele yumuşak zoom/pan yap (id = .parcel[data-id]). Yoksa no-op. */
  focusParcel(id: string): void;
}

/**
 * SVG harita etkileşimi: sürükle-kaydır, tekerlek/buton/çift-tık/pinch zoom,
 * parsel tıklama. Pan/zoom `bounds` içine KİLİTLİ — harita dışına (boş deniz)
 * kaydırılamaz, sonsuz scroll yok.
 */
export function attachSvgInteraction(
  svg: SVGSVGElement,
  handlers: SvgInteractionHandlers,
  opts: SvgInteractionOptions,
): MapController {
  const vb = svg.viewBox.baseVal;
  const b = opts.bounds;
  const minW = opts.minWidth ?? Math.max(120, b.width * 0.12);

  // Container oranı — her zaman buna uy (letterbox/kenar boşluğu olmasın)
  const curA = () => {
    const w = svg.clientWidth, h = svg.clientHeight;
    return w > 0 && h > 0 ? w / h : vb.width / vb.height;
  };
  // En uzak zoom = tüm marina genişliği (her şey görünsün)
  const maxWidth = () => b.width;
  // Logolar bu genişlikten daha yakınken görünür (daha uzaktan görünsün)
  const logoThreshold = b.width * 0.66;

  // viewBox'ı container oranına + sınırlara oturt.
  // - Dar/normal görünüm: tamamen sınır içinde (kenarda boşluk yok)
  // - Geniş "tümünü gör" görünümü: alt kenar KARADA sabit, fazlalık üste (su) gider
  const clamp = () => {
    const A = curA();
    let nw = Math.min(Math.max(vb.width, minW), maxWidth());
    vb.width = nw;
    vb.height = nw / A;
    // yatay: sınır içinde
    vb.x = Math.min(Math.max(vb.x, b.x), Math.max(b.x, b.x + b.width - vb.width));
    // dikey: sığıyorsa sınır içinde, sığmıyorsa alt = kara (üst su)
    if (vb.height <= b.height) {
      vb.y = Math.min(Math.max(vb.y, b.y), b.y + b.height - vb.height);
    } else {
      vb.y = b.y + b.height - vb.height; // alt kenar kara, üst su
    }
    // Yakınlaşınca logoları göster
    svg.classList.toggle("zoomed", vb.width < logoThreshold);
  };

  const pointers = new Map<number, { x: number; y: number }>();
  let moved = false, startX = 0, startY = 0, downLocationId: string | null = null;
  let downLevel: string | undefined;
  let downCategory: string | undefined;
  let pinchDist = 0;

  const unitPerPx = () => {
    const m = svg.getScreenCTM();
    return m ? 1 / m.a : vb.width / svg.clientWidth;
  };
  const clientToVB = (cx: number, cy: number): [number, number] => {
    const m = svg.getScreenCTM();
    if (!m) return [vb.x + vb.width / 2, vb.y + vb.height / 2];
    const pt = svg.createSVGPoint();
    pt.x = cx; pt.y = cy;
    const p = pt.matrixTransform(m.inverse());
    return [p.x, p.y];
  };
  const zoomBy = (factor: number, cx: number, cy: number) => {
    const nw = Math.min(maxWidth(), Math.max(minW, vb.width * factor));
    const f = nw / vb.width;
    vb.x = cx - (cx - vb.x) * f;
    vb.y = cy - (cy - vb.y) * f;
    vb.width *= f;
    vb.height *= f;
    clamp();
  };
  const center = (): [number, number] => [vb.x + vb.width / 2, vb.y + vb.height / 2];

  // Yumuşak zoom/pan animasyonu (buton/reset/çift-tık için)
  let raf = 0;
  const tweenTo = (tx: number, ty: number, tw: number, th: number, dur = 260) => {
    cancelAnimationFrame(raf);
    const sx = vb.x, sy = vb.y, sw = vb.width, sh = vb.height;
    const t0 = performance.now();
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);
    const step = (now: number) => {
      const t = Math.min(1, (now - t0) / dur), e = ease(t);
      vb.x = sx + (tx - sx) * e; vb.y = sy + (ty - sy) * e;
      vb.width = sw + (tw - sw) * e; vb.height = sh + (th - sh) * e;
      svg.classList.toggle("zoomed", vb.width < logoThreshold);
      if (t < 1) raf = requestAnimationFrame(step); else clamp();
    };
    raf = requestAnimationFrame(step);
  };
  const zoomTarget = (factor: number, cx: number, cy: number) => {
    const nw = Math.min(maxWidth(), Math.max(minW, vb.width * factor));
    const f = nw / vb.width;
    return { x: cx - (cx - vb.x) * f, y: cy - (cy - vb.y) * f, w: vb.width * f, h: vb.height * f };
  };
  const animZoom = (factor: number, cx: number, cy: number) => {
    const t = zoomTarget(factor, cx, cy);
    tweenTo(t.x, t.y, t.w, t.h);
  };

  svg.addEventListener("pointerdown", (e) => {
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    svg.setPointerCapture(e.pointerId);
    if (pointers.size === 1) {
      moved = false;
      startX = e.clientX; startY = e.clientY;
      const badge = (e.target as Element)?.closest?.(".parcel-logo") as SVGElement | null;
      if (badge) {
        downLocationId = badge.dataset.id ?? null;
        downLevel = badge.dataset.level || undefined;
        downCategory = badge.dataset.category || undefined;
      } else {
        // Parsel gövdesi: filtrenin o parsel için seçtiği kiracıyı aç (rozetle aynı).
        const parcel = (e.target as Element)?.closest?.(".parcel") as SVGElement | null;
        downLocationId = parcel?.getAttribute("data-location-id") ?? null;
        downLevel = parcel?.dataset.activeLevel || undefined;
        downCategory = parcel?.dataset.activeCategory || undefined;
      }
      svg.classList.add("dragging");
    } else if (pointers.size === 2) {
      const [a, c] = [...pointers.values()];
      pinchDist = Math.hypot(a.x - c.x, a.y - c.y);
      moved = true;
    }
  });

  svg.addEventListener("pointermove", (e) => {
    if (!pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size >= 2) {
      const [a, c] = [...pointers.values()];
      const dist = Math.hypot(a.x - c.x, a.y - c.y);
      if (pinchDist > 0) {
        const [vx, vy] = clientToVB((a.x + c.x) / 2, (a.y + c.y) / 2);
        zoomBy(pinchDist / dist, vx, vy);
      }
      pinchDist = dist;
      return;
    }
    const dx = e.clientX - startX, dy = e.clientY - startY;
    if (Math.hypot(dx, dy) > 3) moved = true;
    const k = unitPerPx();
    vb.x -= dx * k;
    vb.y -= dy * k;
    startX = e.clientX; startY = e.clientY;
    clamp();
  });

  const end = (e: PointerEvent) => {
    if (!pointers.has(e.pointerId)) return;
    pointers.delete(e.pointerId);
    try { svg.releasePointerCapture(e.pointerId); } catch { /* yoksay */ }
    if (pointers.size < 2) pinchDist = 0;
    if (pointers.size === 0) {
      svg.classList.remove("dragging");
      if (!moved) {
        if (downLocationId) handlers.onParcelClick(downLocationId, downLevel, downCategory);
        else handlers.onEmptyClick?.();
      }
      downLocationId = null;
      downLevel = undefined;
      downCategory = undefined;
    }
  };
  svg.addEventListener("pointerup", end);
  svg.addEventListener("pointercancel", end);

  svg.addEventListener("wheel", (e) => {
    e.preventDefault();
    const [vx, vy] = clientToVB(e.clientX, e.clientY);
    zoomBy(e.deltaY > 0 ? 1.12 : 1 / 1.12, vx, vy);
  }, { passive: false });

  svg.addEventListener("dblclick", (e) => {
    e.preventDefault();
    const [vx, vy] = clientToVB(e.clientX, e.clientY);
    animZoom(1 / 1.6, vx, vy); // yumuşak yakınlaş
  });

  window.addEventListener("resize", clamp); // pencere değişince oranı koru

  clamp(); // başlangıç görünümünü de sınıra oturt
  const initial = { x: vb.x, y: vb.y, width: vb.width, height: vb.height };

  // Bir parseli ekrana ortalayıp rahat bir zoom seviyesine uçur (arama vb. için).
  const focusParcel = (id: string) => {
    const el = svg.querySelector<SVGGraphicsElement>(`.parcel[data-id="${CSS.escape(id)}"]`);
    if (!el) return;
    const bb = el.getBBox();
    const A = curA();
    // Parseli rahat dolduran genişlik: bbox + pay, minW/maxWidth ile sınırlı
    const PAD = 2.2;
    const tw = Math.min(maxWidth(), Math.max(minW, Math.max(bb.width, bb.height * A) * (1 + PAD)));
    const th = tw / A;
    let tx = bb.x + bb.width / 2 - tw / 2;
    let ty = bb.y + bb.height / 2 - th / 2;
    // sınıra oturt (clamp ile aynı mantık)
    tx = Math.min(Math.max(tx, b.x), Math.max(b.x, b.x + b.width - tw));
    if (th <= b.height) ty = Math.min(Math.max(ty, b.y), b.y + b.height - th);
    else ty = b.y + b.height - th;
    tweenTo(tx, ty, tw, th);
  };

  return {
    zoomIn: () => animZoom(1 / 1.5, ...center()),
    zoomOut: () => animZoom(1.5, ...center()),
    reset: () => tweenTo(initial.x, initial.y, initial.width, initial.height),
    focusParcel,
  };
}
